import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Table, TTableColumn, DateTools, Icon, StatusBadge, Pagination } from '@treezor/design-system';

import { useCompanyMembers } from '../hooks';

import { IUser } from '@/features/user/store/user.model';

import { COMPANY_MEMBERS_PER_PAGE } from '@/hooks/useDashboardConstants';

import { DashboardHelpers } from '@/utils/DashboardHelpers';

import { KycStatus } from '@/features/kyc/components/kyc-infos/KycStatus';

import { EmptyState } from '@/ui-kit/templates/EmptyState/EmptyState';

import { CompanyMemberLoader } from './CompanyMemberLoader';

type TCompanyMemberProps = {
  onPageChange: (pageNumber: number) => void;
};

export function CompanyMemberTable({ onPageChange }: TCompanyMemberProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { watch } = useFormContext();

  const filterId = watch('userId');
  const filterName = watch('name');
  const filterStatus = watch('status');

  const { members = [], totalRows, hasAllItems, isPhysicalUser, isLoading, error } = useCompanyMembers();
  const filteredMembers = useMemo(() => {
    let result = members;
    if (hasAllItems) {
      if (filterName) {
        result = result.filter((member) =>
          DashboardHelpers.getUserName(member)?.toLowerCase().includes(filterName.toLowerCase()),
        );
      }

      if (filterId) {
        result = result.filter((member) => member.userId?.toString().includes(filterId.toLowerCase()));
      }

      if (filterStatus?.length > 0) {
        result = result.filter((member) => member.userStatus === filterStatus[0]);
      }
    }

    return result;
  }, [members, filterId, filterName, filterStatus, hasAllItems]);

  const rows = filteredMembers?.map((member: IUser) => ({
    user: member,
    kycStatus: '',
    createdDate: member.createdDate,
    onClick: () => navigate(DashboardHelpers.generateProfilePath(member.userId)),
  }));

  type TData = (typeof rows)[number];

  const columns: TTableColumn<TData>[] = [
    {
      label: t('pages.dashboard.users.table.userRows', { length: filteredMembers.length }),
      field: 'user',
      name: 'user',
      cellRenderer: (cell) => (
        <div className="flex flex-col">
          <span className="typo-body-semibold-small text-secondary">{DashboardHelpers.getUserName(cell.user)}</span>
          <span className="typo-body-regular-small text-tertiary">#{cell.user.userId}</span>
        </div>
      ),
      ...(hasAllItems ? { isSortable: true } : {}),
    },
    {
      label: isPhysicalUser ? t('pages.dashboard.users.table.userTypeId') : t('pages.dashboard.users.table.role'),
      field: 'user',
      name: 'userType',
      cellRenderer: (cell) => (
        <div className="flex gap-2 items-center">
          <Icon name="person" size="s" />
          <span className="typo-body-regular-small text-secondary">
            {DashboardHelpers.getUserRole(cell.user)
              ? t(`pages.dashboard.users.physicalUserRole.${DashboardHelpers.getUserRole(cell.user)}`)
              : t('pages.dashboard.userCreation.userTypes.physical_user')}
          </span>
        </div>
      ),
      ...(hasAllItems ? { isSortable: true } : {}),
    },

    {
      label: t('pages.dashboard.users.table.userStatus'),
      field: 'user',
      name: 'status',
      cellRenderer: (cell) => {
        const userStatusProps = DashboardHelpers.getUserStatus(cell.user);
        return <StatusBadge label={userStatusProps?.label} color={userStatusProps?.color} emphasis="low" size="s" />;
      },
      ...(hasAllItems ? { isSortable: true } : {}),
    },

    {
      label: t('pages.dashboard.users.table.kycStatus'),
      field: 'kycStatus',
      name: 'kycStatus',
      cellRenderer: (cell) => <KycStatus user={cell.user} />,
      ...(hasAllItems ? { isSortable: true } : {}),
    },
    {
      label: t('pages.dashboard.users.table.creationDate'),
      field: 'createdDate',
      name: 'createdDate',
      cellRenderer: (cell) => (
        <span className="text-nowrap text-secondary typo-body-regular-small">
          {DateTools.format(cell.createdDate, 'date-time') || ' - '}
        </span>
      ),
      ...(hasAllItems ? { isSortable: true } : {}),
    },
  ];

  if (error)
    return (
      <div className="flex flex-col items-center w-full gap-3 p-7">
        <h4 className="typo-h4-bold text-error">{t('pages.cores.error')}</h4>
        <p className="typo-body-regular-paragraph text-helper">
          {t('pages.currentProfiles.profile.tabs.title.errorMessage')}
        </p>
      </div>
    );
  if (isLoading) return <CompanyMemberLoader />;

  return (
    <>
      <Table
        columns={columns}
        rows={rows}
        emptyComponent={
          <EmptyState title={t('components.molecules.table.noRecord')} icon="search_v1" size="m" className="p-6" />
        }
      />
      {totalRows > COMPANY_MEMBERS_PER_PAGE && !hasAllItems && (
        <Pagination
          isRangeVisible
          itemCount={totalRows}
          activePage={watch('pageNumber')}
          onPageChange={(item) => onPageChange(item.selected)}
          itemPerPage={COMPANY_MEMBERS_PER_PAGE}
          isFirstButtonVisible
          isLastButtonVisible
          className="flex justify-end"
        />
      )}
    </>
  );
}
export default CompanyMemberTable;
